import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/common";
import { CareEquipmentForm } from "@/components/CareEquipmentForm";
import { WorkOrderDialog } from "@/components/WorkOrderDialog";
import { STATE_META, CATEGORY_ICON, DUE_META, careFileUrl } from "@/lib/care";
import { fmtDate, fmtNum } from "@/lib/helpers";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AnomalyList } from "@/components/care/AnomalyList";
import { ANOMALY_FLAG } from "@/lib/sav";
import { toast } from "sonner";
import * as Icons from "lucide-react";

function StateBadge({ status }) {
  const m = STATE_META[status] || STATE_META.non_evalue;
  return (
    <span data-testid={`care-state-${status}`} className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${m.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />{m.label}
    </span>
  );
}

export default function Care() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPerm } = useAuth();
  const canManage = hasPerm("care.manage");
  const canControl = hasPerm("care.control");
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [filters, setFilters] = useState({ client_id: "", site_id: "", category: "", status: "", echeance: searchParams.get("echeance") === "due" ? "due" : "", search: "" });
  const [openNew, setOpenNew] = useState(false);
  const [tab, setTab] = useState(searchParams.get("tab") === "anomalies" ? "anomalies" : "equipment");
  const [anCount, setAnCount] = useState(0);
  useEffect(() => { api.get("/care/anomalies/count").then((r) => setAnCount(r.data.total)).catch(() => {}); }, [tab]);

  useEffect(() => {
    api.get("/care/meta").then((r) => setMeta(r.data)).catch(() => toast.error("Impossible de charger les référentiels SIGNA CARE"));
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (filters.client_id) api.get("/sites", { params: { client_id: filters.client_id } }).then((r) => setSites(r.data));
    else setSites([]);
  }, [filters.client_id]);

  const load = useCallback(async () => {
    setError(false); setData(null);
    const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v && v !== "all"));
    try { const { data } = await api.get("/care/equipment", { params: clean }); setData(data); }
    catch { setError(true); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v === "all" ? "" : v, ...(k === "client_id" ? { site_id: "" } : {}) }));

  const create = async (payload) => {
    try { const { data } = await api.post("/care/equipment", payload); toast.success("Équipement ajouté"); setOpenNew(false); navigate(`/care/${data.id}`); }
    catch (e) { toast.error(apiError(e)); }
  };

  const counts = data?.counts || {};
  const STAT_CARDS = [
    { key: "total", label: "Équipements", val: data?.total, icon: "Boxes", color: "text-slate-700 bg-slate-100" },
    { key: "bon_etat", label: "Bon état apparent", val: counts.bon_etat, icon: "CircleCheck", color: "text-emerald-600 bg-emerald-50" },
    { key: "a_controler", label: "À contrôler", val: counts.a_controler, icon: "Search", color: "text-amber-600 bg-amber-50" },
    { key: "due", label: "Contrôle à échéance", val: data?.due, icon: "CalendarClock", color: "text-rose-600 bg-rose-50" },
  ];

  return (
    <div className="space-y-5" data-testid="care-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0D2E24] lg:text-3xl">SIGNA CARE</h1>
          <p className="text-sm text-slate-500">Registre et suivi des équipements.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canControl && filters.site_id && <WorkOrderDialog scope="site" siteId={filters.site_id} />}
          {canManage && (
            <Button onClick={() => setOpenNew(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="care-add-btn">
              <Icons.Plus className="mr-1.5 h-4 w-4" /> Ajouter un équipement
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200" data-testid="care-tabs">
        {[["equipment", "Équipements"], ["anomalies", "Anomalies équipements"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`care-tab-${k}`}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${tab === k ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {l}{k === "anomalies" && anCount > 0 && <span className="rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white" data-testid="care-tab-anomalies-badge">{anCount}</span>}
          </button>))}
      </div>

      {tab === "anomalies" ? <AnomalyList /> : <>
      <div className="rounded-lg border border-slate-200 bg-white p-2.5 text-xs text-slate-500" data-testid="care-disclaimer">
        <Icons.Info className="mr-1.5 inline h-3.5 w-3.5 text-slate-400" />
        Registre de suivi : les états sont des constats observés et ne valent pas certification de conformité.
      </div>

      {/* Compteurs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {STAT_CARDS.map((c) => {
          const I = Icons[c.icon];
          const val = error ? "!" : (data ? (c.val ?? 0) : null);
          const clickable = c.key === "due" || ["bon_etat", "a_controler"].includes(c.key);
          return (
            <Card key={c.key} onClick={clickable ? () => (c.key === "due" ? setF("echeance", "due") : setF("status", c.key)) : undefined}
              className={`flex items-center gap-3 p-3 sm:p-4 ${clickable ? "cursor-pointer transition-shadow hover:shadow-md" : ""}`} data-testid={`care-stat-${c.key}`}>
              <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${c.color}`}><I className="h-5 w-5" /></span>
              <div className="min-w-0">
                <p className="font-mono-data text-2xl font-bold leading-tight text-slate-900">
                  {val === null ? <span className="inline-block h-6 w-6 animate-pulse rounded bg-slate-200 align-middle" /> : val}
                </p>
                <p className="truncate text-xs text-slate-500 sm:text-sm">{c.label}</p>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Filtres */}
      <Card className="p-3">
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-6">
          <div className="relative col-span-2 lg:col-span-1">
            <Icons.Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={filters.search} onChange={(e) => setF("search", e.target.value)} placeholder="Référence, nom…" className="pl-8" data-testid="care-filter-search" />
          </div>
          <Select value={filters.client_id || "all"} onValueChange={(v) => setF("client_id", v)}>
            <SelectTrigger data-testid="care-filter-client"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous les clients</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.site_id || "all"} onValueChange={(v) => setF("site_id", v)} disabled={!filters.client_id}>
            <SelectTrigger data-testid="care-filter-site"><SelectValue placeholder="Site" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous les sites</SelectItem>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.category || "all"} onValueChange={(v) => setF("category", v)}>
            <SelectTrigger data-testid="care-filter-category"><SelectValue placeholder="Catégorie" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Toutes catégories</SelectItem>{Object.entries(meta?.categories || {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.status || "all"} onValueChange={(v) => setF("status", v)}>
            <SelectTrigger data-testid="care-filter-status"><SelectValue placeholder="État" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous les états</SelectItem>{Object.entries(meta?.states || {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant={filters.echeance === "due" ? "default" : "outline"} onClick={() => setF("echeance", filters.echeance === "due" ? "" : "due")}
            className={filters.echeance === "due" ? "bg-rose-600 hover:bg-rose-700" : ""} data-testid="care-filter-echeance">
            <Icons.CalendarClock className="mr-1.5 h-4 w-4" /> Contrôle à échéance
          </Button>
        </div>
      </Card>

      {/* Liste */}
      {error ? (
        <Card className="p-6 text-center text-sm text-rose-600" data-testid="care-error">Impossible de charger les équipements. Réessayez plus tard.</Card>
      ) : !data ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <Card key={i} className="h-36 animate-pulse bg-slate-100" />)}</div>
      ) : data.items.length === 0 ? (
        <EmptyState icon="Boxes" title="Aucun équipement" testId="care-empty">
          {Object.values(filters).some(Boolean) ? "Aucun équipement ne correspond aux filtres." : "Ajoutez le premier équipement à suivre."}
        </EmptyState>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.items.map((e) => {
            const CI = Icons[CATEGORY_ICON[e.category] || "Package"];
            const photo = e.photo_count > 0;
            return (
              <Card key={e.id} onClick={() => navigate(`/care/${e.id}`)} className="cursor-pointer overflow-hidden transition-shadow hover:shadow-md" data-testid={`care-card-${e.id}`}>
                <div className="flex h-28 items-center justify-center bg-slate-100 text-slate-300">
                  {photo ? <img src={careFileUrl(e.first_photo_id)} alt="" className="h-full w-full object-cover" /> : <CI className="h-9 w-9" />}
                </div>
                <div className="p-3.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-slate-800"><span className="font-mono-data text-xs text-slate-400">{e.reference}</span></p>
                      <p className="truncate text-sm text-slate-600">{e.name}</p>
                    </div>
                    {e.due_status && DUE_META[e.due_status] && (
                      <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${DUE_META[e.due_status].cls}`}>{DUE_META[e.due_status].label}</span>
                    )}
                  </div>
                  <p className="mt-1 truncate text-xs text-slate-400">{e.category_label}{e.zone ? ` · ${e.zone}` : ""}{e.location_desc ? ` · ${e.location_desc}` : ""}</p>
                  {e.mode === "ensemble" && <p className="mt-0.5 text-xs text-slate-500">Ensemble : {fmtNum(e.quantity)} {meta?.units?.[e.unit] || e.unit}</p>}
                  <div className="mt-2 flex items-center justify-between">
                    <StateBadge status={e.status} />
                    <span className="text-[11px] text-slate-400">{e.last_control_date ? `Contrôle ${fmtDate(e.last_control_date)}` : "Aucun contrôle enregistré"}</span>
                  </div>
                  {e.next_check_date && <p className="mt-1 text-[11px] text-slate-400">Prochaine échéance : {fmtDate(e.next_check_date)}</p>}
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {e.open_anomalies > 0 && <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-medium text-rose-700" data-testid={`care-card-anomaly-${e.id}`}>{ANOMALY_FLAG}</span>}
                    {e.client_visible && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-700">Visible client</span>}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      </>}

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="care-new-dialog">
          <DialogHeader><DialogTitle>Nouvel équipement</DialogTitle></DialogHeader>
          {meta && <CareEquipmentForm meta={meta} onSave={create} onCancel={() => setOpenNew(false)} testPrefix="new-eq" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
